(() => {
  document.documentElement.classList.remove('no-js');

  const body = document.body;
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  function formatMoney(cents) {
    const format = (window.PakDropsTheme && window.PakDropsTheme.moneyFormat) || 'Rs. {{amount}}';
    const number = Number(cents || 0) / 100;
    const withDecimals = number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const noDecimals = Math.round(number).toLocaleString();
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals.replace(/,/g, 'X').replace(/\./g, ',').replace(/X/g, '.'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals.replace(/,/g, '.'))
      .replace(/\{\{\s*amount\s*\}\}/g, withDecimals);
  }

  function lockBody(className, active) {
    body.classList.toggle(className, active);
  }

  function closeMobileMenu() {
    const menu = qs('[data-mobile-menu]');
    if (!menu) return;
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    lockBody('menu-open', false);
  }

  function openMobileMenu() {
    const menu = qs('[data-mobile-menu]');
    if (!menu) return;
    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    lockBody('menu-open', true);
  }

  function closeSearch() {
    const overlay = qs('[data-search-overlay]');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    lockBody('search-open', false);
  }

  function openSearch() {
    const overlay = qs('[data-search-overlay]');
    if (!overlay) return;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    lockBody('search-open', true);
    setTimeout(() => qs('[data-search-input]', overlay)?.focus(), 120);
  }

  function closeCart() {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    lockBody('drawer-open', false);
  }

  function openCart() {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockBody('drawer-open', true);
  }

  async function refreshCartCount() {
    try {
      const response = await fetch(`${window.PakDropsTheme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      qsa('[data-cart-count]').forEach((node) => {
        node.textContent = cart.item_count;
        node.hidden = cart.item_count === 0;
      });
      return cart;
    } catch (error) {
      console.warn('Cart count refresh failed', error);
    }
  }

  async function refreshCartDrawer(shouldOpen = true) {
    try {
      const response = await fetch(`${window.PakDropsTheme.routes.cart}?section_id=cart-drawer`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Cart section request failed');
      const html = await response.text();
      const temp = document.createElement('div');
      temp.innerHTML = html;
      const nextDrawer = qs('[data-cart-drawer]', temp);
      const currentDrawer = qs('[data-cart-drawer]');
      if (nextDrawer && currentDrawer) currentDrawer.replaceWith(nextDrawer);
      await refreshCartCount();
      if (shouldOpen) openCart();
    } catch (error) {
      console.warn('Cart drawer refresh failed', error);
      window.location.href = window.PakDropsTheme.routes.cart;
    }
  }

  async function changeCartLine(line, quantity) {
    try {
      await fetch(`${window.PakDropsTheme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity)) })
      });
      await refreshCartDrawer(true);
    } catch (error) {
      console.warn('Cart update failed', error);
    }
  }

  async function addProduct(form, submitButton) {
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('is-loading');
    }
    try {
      const formData = new FormData(form);
      const response = await fetch(`${window.PakDropsTheme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || 'Unable to add this item');
      }
      await refreshCartDrawer(true);
    } catch (error) {
      alert(error.message || 'Unable to add this item to cart.');
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove('is-loading');
      }
    }
  }

  function closeFilters() {
    lockBody('filter-open', false);
  }

  function openFilters() {
    lockBody('filter-open', true);
  }

  function selectGalleryMedia(root, mediaId) {
    qsa('[data-gallery-media]', root).forEach((media) => media.classList.toggle('is-active', String(media.dataset.galleryMedia) === String(mediaId)));
    qsa('[data-gallery-thumb]', root).forEach((thumb) => thumb.classList.toggle('is-active', String(thumb.dataset.mediaId) === String(mediaId)));
  }

  function initProduct(root) {
    if (!root || root.dataset.productInitialized === 'true') return;
    root.dataset.productInitialized = 'true';

    const jsonNode = qs('[data-product-variants]', root);
    let variants = [];
    try { variants = jsonNode ? JSON.parse(jsonNode.textContent) : []; } catch (e) { variants = []; }

    const optionGroups = qsa('[data-option-index]', root);
    const selectedOptions = optionGroups.map((group) => qs('.variant-value.is-selected', group)?.dataset.optionValue || '');
    const idInput = qs('[data-variant-id]', root);
    const priceNode = qs('[data-product-price]', root);
    const compareNode = qs('[data-product-compare]', root);
    const saleNode = qs('[data-product-sale]', root);
    const addButton = qs('[data-add-to-cart]', root);
    const addLabel = qs('[data-add-label]', root);
    const addPrice = qs('[data-add-price]', root);
    const stickyPrice = qs('[data-sticky-price]');
    const stickyButton = qs('[data-sticky-add]');

    function matchingVariant(options) {
      return variants.find((variant) => variant.options.every((value, index) => value === options[index]));
    }

    function refreshOptionAvailability() {
      optionGroups.forEach((group, groupIndex) => {
        qsa('[data-option-value]', group).forEach((button) => {
          const candidate = [...selectedOptions];
          candidate[groupIndex] = button.dataset.optionValue;
          const exists = variants.some((variant) => variant.available && variant.options.every((value, index) => {
            if (index > groupIndex && !candidate[index]) return true;
            return value === candidate[index];
          }));
          button.classList.toggle('is-unavailable', !exists);
          button.setAttribute('aria-disabled', String(!exists));
        });
      });
    }

    function applyVariant(variant) {
      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
        if (addPrice) addPrice.textContent = '';
        if (stickyButton) { stickyButton.disabled = true; stickyButton.textContent = 'Unavailable'; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (priceNode) priceNode.textContent = formatMoney(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      const hasCompare = Number(variant.compare_at_price) > Number(variant.price);
      if (compareNode) {
        compareNode.hidden = !hasCompare;
        compareNode.textContent = hasCompare ? formatMoney(variant.compare_at_price) : '';
      }
      if (saleNode) saleNode.hidden = !hasCompare;
      if (addButton) addButton.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
      if (addPrice) addPrice.textContent = variant.available ? `• ${formatMoney(variant.price)}` : '';
      if (stickyButton) { stickyButton.disabled = !variant.available; stickyButton.textContent = variant.available ? 'Add to cart' : 'Sold out'; }
      if (variant.featured_media_id) selectGalleryMedia(root, variant.featured_media_id);
      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    }

    optionGroups.forEach((group, index) => {
      qsa('[data-option-value]', group).forEach((button) => {
        button.addEventListener('click', () => {
          selectedOptions[index] = button.dataset.optionValue;
          qsa('[data-option-value]', group).forEach((item) => item.classList.toggle('is-selected', item === button));
          const selectedLabel = qs('[data-option-selected]', group);
          if (selectedLabel) selectedLabel.textContent = button.dataset.optionValue;
          refreshOptionAvailability();
          applyVariant(matchingVariant(selectedOptions));
        });
      });
    });

    qsa('[data-gallery-thumb]', root).forEach((button) => button.addEventListener('click', () => selectGalleryMedia(root, button.dataset.mediaId)));
    refreshOptionAvailability();

    const qtyRoot = qs('[data-quantity-selector]', root);
    if (qtyRoot) {
      const input = qs('[data-qty-input]', qtyRoot);
      qs('[data-qty-minus]', qtyRoot)?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value || 1) - 1); });
      qs('[data-qty-plus]', qtyRoot)?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value || 1) + 1); });
    }

    if (stickyButton && addButton) stickyButton.addEventListener('click', () => addButton.click());

    const stickyBar = qs('[data-mobile-sticky-atc]');
    if (stickyBar && addButton && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => stickyBar.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0.15 });
      observer.observe(addButton);
    }
  }

  function initReveal(root = document) {
    const nodes = qsa('.reveal-on-scroll:not(.is-visible)', root);
    if (!('IntersectionObserver' in window)) { nodes.forEach((node) => node.classList.add('is-visible')); return; }
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -40px', threshold: 0.08 });
    nodes.forEach((node) => observer.observe(node));
  }

  function initRecommendations() {
    qsa('product-recommendations:not([data-loaded])').forEach(async (element) => {
      element.dataset.loaded = 'true';
      if (!element.dataset.url) return;
      try {
        const response = await fetch(element.dataset.url);
        const html = await response.text();
        const temp = document.createElement('div');
        temp.innerHTML = html;
        const recommendations = qs('product-recommendations', temp);
        if (recommendations && recommendations.innerHTML.trim()) {
          element.innerHTML = recommendations.innerHTML;
          initReveal(element);
        }
      } catch (error) {
        console.warn('Recommendations unavailable', error);
      }
    });
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-mobile-menu-open]')) openMobileMenu();
    if (event.target.closest('[data-mobile-menu-close]') || (event.target.matches('[data-mobile-menu]'))) closeMobileMenu();
    if (event.target.closest('[data-search-open]')) openSearch();
    if (event.target.closest('[data-search-close]') || (event.target.matches('[data-search-overlay]'))) closeSearch();

    const cartOpen = event.target.closest('[data-cart-open]');
    if (cartOpen) { event.preventDefault(); openCart(); }
    if (event.target.closest('[data-cart-close]')) closeCart();

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) changeCartLine(removeButton.dataset.cartRemove, 0);
    const plusButton = event.target.closest('[data-cart-qty-plus]');
    if (plusButton) {
      const item = plusButton.closest('[data-cart-line]');
      const current = Number(qs('.quantity-selector span', item)?.textContent || 1);
      changeCartLine(plusButton.dataset.cartQtyPlus, current + 1);
    }
    const minusButton = event.target.closest('[data-cart-qty-minus]');
    if (minusButton) {
      const item = minusButton.closest('[data-cart-line]');
      const current = Number(qs('.quantity-selector span', item)?.textContent || 1);
      changeCartLine(minusButton.dataset.cartQtyMinus, Math.max(0, current - 1));
    }

    if (event.target.closest('[data-filter-open]')) openFilters();
    if (event.target.closest('[data-filter-close]')) closeFilters();

    const pagePlus = event.target.closest('[data-cart-page-plus]');
    if (pagePlus) {
      const input = qs('input[type="number"]', pagePlus.closest('.quantity-selector'));
      if (input) input.value = Math.max(1, Number(input.value || 1) + 1);
    }
    const pageMinus = event.target.closest('[data-cart-page-minus]');
    if (pageMinus) {
      const input = qs('input[type="number"]', pageMinus.closest('.quantity-selector'));
      if (input) input.value = Math.max(0, Number(input.value || 1) - 1);
    }

    if (event.target.closest('[data-back-to-top]')) window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  document.addEventListener('submit', (event) => {
    const quickForm = event.target.closest('[data-quick-add-form]');
    const productForm = event.target.closest('[data-ajax-product-form]');
    if (!quickForm && !productForm) return;
    if (productForm && event.submitter && !event.submitter.matches('[data-add-to-cart]')) return;
    event.preventDefault();
    addProduct(event.target, event.submitter || qs('button[type="submit"]', event.target));
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-sort-select]')) event.target.closest('form')?.submit();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { closeMobileMenu(); closeSearch(); closeCart(); closeFilters(); }
  });

  window.addEventListener('scroll', () => {
    qs('[data-site-header]')?.classList.toggle('is-scrolled', window.scrollY > 20);
    qs('[data-back-to-top]')?.classList.toggle('is-visible', window.scrollY > 700);
  }, { passive: true });

  function initTheme(root = document) {
    qsa('[data-product-root]', root).forEach(initProduct);
    initReveal(root);
    initRecommendations();
  }

  document.addEventListener('DOMContentLoaded', () => initTheme());
  document.addEventListener('shopify:section:load', (event) => initTheme(event.target));
})();
